import { createContext, useState, useEffect, useContext, useMemo } from "react"
import { useGlobalContext, decryptUsername } from "./GlobalContext"

const LeaderboardContext = createContext()

export const LeaderboardProviderBNG = ({ children }) => {
  const { username } = useGlobalContext()
  const [leaderboardData, setLeaderboardData] = useState([])
  const [loadingBoard, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [playerPosition, setPlayerPosition] = useState(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [boardMode, setBoardMode] = useState("slots")
  const DEBUG = false

  useEffect(() => {
    const loggedInState = localStorage.getItem("isLoggedIn") === "true"
    setIsLoggedIn(loggedInState)
  }, [])

  const getBonusByRank = (rank) => {
    const r = parseInt(rank, 10)
    if (r === 1) return 300
    if (r === 2) return 250
    if (r === 3) return 200
    if (r >= 4 && r <= 10) return 100
    if (r >= 11 && r <= 15) return 50
    if (r >= 16 && r <= 20) return 30

    return 0
  }

  const maskUsernameExactKeepLast = (u) => {
    if (!u) return u

    const normalized = (u + "###########").slice(0, 11)
    const lastChar = u.slice(-2)

    return (
      normalized.slice(0, 5) + // first 5
      "**" +
      normalized.charAt(7) + // fixed pos
      "*" +
      lastChar // ALWAYS read last char
    )
  }

  const getPlayerPosition = () => {
    const storedUsername = decryptUsername(localStorage.getItem("username"))
    if (!storedUsername) return null

    if (DEBUG) {
      console.log("[DEBUG] Matching storedUsername:", storedUsername)
      console.log(
        "[DEBUG] Againts leaderboard randomChars:",
        leaderboardData.map((e) => e.randomChars)
      )
    }

    const found = leaderboardData.find(
      (entry) => entry.randomChars === storedUsername
    )

    return found ? parseInt(found.ranking, 20) : null
  }

  useEffect(() => {
    const pos = getPlayerPosition()
    if (pos !== null) setPlayerPosition(pos)
  }, [leaderboardData])

  useEffect(() => {
    if (!isLoggedIn) return
    const fetchLeaderboardData = async () => {
      try {
        setLoading(true)
        const url = `${process.env.REACT_APP_API_JSON_URL_PROMO}?cmd=getBNGLeaderboard`
        if (DEBUG) console.log("[DEBUG] Fetching leaderboard from:", url)

        const response = await fetch(url)
        if (!response.ok) throw new Error(`HTTP error: ${response.status}`)

        const rawData = await response.json()
        if (DEBUG) console.log("[DEBUG] Raw leaderboard data:", rawData)

        const parsedData = await Promise.all(
          Object.entries(rawData).map(async ([ranking, value]) => {
            const parts = value.split("|")
            const realUsername = parts[0]?.trim() || ""
            const freespins = getBonusByRank(ranking)

            const randomChars = realUsername
            const masked = realUsername

            if (DEBUG) {
              console.log(`[DEBUG] Rank ${ranking}:`, {
                username,
                freespins,
                randomChars,
                masked,
              })
            }

            return {
              ranking,
              username,
              freespins,
              randomChars,
              masked,
            }
          })
        )

        if (DEBUG) console.log("[DEBUG] Parsed leaderboard data:", parsedData)
        setLeaderboardData(parsedData.slice(0, 20))
      } catch (err) {
        console.error("Leaderboard API error:", err)
        setError(err)
      } finally {
        setLoading(false)
      }
    }
    fetchLeaderboardData()
  }, [isLoggedIn])

  return (
    <>
      <LeaderboardContext.Provider
        value={{
          leaderboardData,
          loadingBoard,
          error,
          getBonusByRank,
          getPlayerPosition,
          playerPosition,
          boardMode,
          setBoardMode,
        }}
      >
        {children}
      </LeaderboardContext.Provider>
    </>
  )
}

export const useLeaderboard = () => useContext(LeaderboardContext)
